import React, { useState, useEffect } from "react";
import companyService from "@/services/companyService";
import { copyTasksToCustomerAssurance } from "@/services/frameworkService";
import { useTenancy } from "@/contexts/TenancyContext";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";

interface ISO9001State {
  enabled: "enabled" | "disabled";
}

const defaultISO9001: ISO9001State = {
  enabled: "disabled",
};

export default function ISO9001Module() {
  const { activeTenancy } = useTenancy();
  const [iso9001, setIso9001] = useState<ISO9001State>(defaultISO9001);
  const [iso9001Initial, setIso9001Initial] = useState<ISO9001State | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchISO9001 = async () => {
      setIsLoading(true);
      try {
        const settings = await companyService.getCompanySettings(undefined, "complianceModules");
        const iso9001Settings = settings?.["iso9001"] ?? {};
        // Precedence: 'Enabled' (case-insensitive), then 'active', 'enabled', 'Active', etc.
        let isEnabled = false;
        // Helper to check both string and boolean
        function checkEnabled(val: unknown): boolean {
          if (typeof val === 'boolean') return val;
          if (typeof val === 'string') return val.toLowerCase() === 'true' || val.toLowerCase() === 'enabled';
          return false;
        }
        if (typeof iso9001Settings.Enabled !== 'undefined') {
          isEnabled = checkEnabled(iso9001Settings.Enabled);
        } else if (typeof iso9001Settings.enabled !== 'undefined') {
          isEnabled = checkEnabled(iso9001Settings.enabled);
        } else if (typeof iso9001Settings.active !== 'undefined') {
          isEnabled = checkEnabled(iso9001Settings.active);
        } else if (typeof iso9001Settings.Active !== 'undefined') {
          isEnabled = checkEnabled(iso9001Settings.Active);
        }
        setIso9001({ enabled: isEnabled ? "enabled" : "disabled" });
        setIso9001Initial({ enabled: isEnabled ? "enabled" : "disabled" });
      } catch (err) {
        toast.error("Failed to fetch ISO 9001 settings");
      } finally {
        setIsLoading(false);
      }
    };
    fetchISO9001();
  }, []);

  const iso9001Changed = iso9001Initial && JSON.stringify(iso9001) !== JSON.stringify(iso9001Initial);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const wasDisabled = iso9001Initial?.enabled === "disabled";
      const isNowEnabled = iso9001.enabled === "enabled";
      
      await companyService.saveCompanySettings(undefined, "complianceModules", {
        iso9001: { Enabled: iso9001.enabled === "enabled" },
      });
      
      // DED-1511: If ISO 9001 is being enabled (was disabled, now enabled), copy tasks
      if (wasDisabled && isNowEnabled && activeTenancy?.id) {
        try {
          const customerId = activeTenancy.id;
          
          toast.info("Copying ISO 9001 tasks...");
          
          await copyTasksToCustomerAssurance(
            customerId,
            'ISO9001',
            undefined,
            undefined
          );
          
          toast.success("ISO 9001 settings saved and tasks copied");
        } catch (taskError) {
          console.error('Error copying ISO 9001 tasks:', taskError);
          toast.warning("Settings saved, but there was an error copying some tasks. Please check the console.");
        }
      } else {
        toast.success("ISO 9001 settings saved");
      }
      
      setIso9001Initial({ ...iso9001 });
    } catch (err) {
      toast.error("Failed to save ISO 9001 settings");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-muted/30 p-6 rounded-lg border border-border space-y-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-green-500/10 p-3 rounded-full">
          <span role="img" aria-label="ISO 9001">🎯</span>
        </div>
        <div>
          <div className="font-semibold text-lg">ISO 9001</div>
          <div className="text-xs text-muted-foreground">Quality Management System Module</div>
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Module Status</label>
        <Select
          value={iso9001.enabled}
          onValueChange={val => setIso9001(v => ({ ...v, enabled: val as ISO9001State["enabled"] }))}
          disabled={isLoading || isSaving}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="flex gap-2 mt-4">
        <Button onClick={handleSave} disabled={!iso9001Changed || isSaving || isLoading}>
          {isSaving ? "Saving..." : "Save ISO 9001 Settings"}
        </Button>
      </div>
    </div>
  );
}
